import { useState, useEffect, useRef } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { RefreshCw, AlertTriangle } from "lucide-react";

interface Props {
  open: boolean;
  /** Called when the user successfully re-authenticates. */
  onSuccess: () => void;
  /** Called when the user dismisses the dialog without re-authenticating. */
  onCancel: () => void;
}

/**
 * Shows a dialog when the Google Drive token has expired or been revoked.
 * Opens the OAuth flow in a popup window and resolves when the popup closes
 * with ?gdrive_connected=1 (the same flag the main app already handles).
 */
export function DriveReauthDialog({ open, onSuccess, onCancel }: Props) {
  const [isConnecting, setIsConnecting] = useState(false);
  const popupRef = useRef<Window | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Clean up polling when dialog closes
  useEffect(() => {
    if (!open) {
      if (pollRef.current) clearInterval(pollRef.current);
      if (popupRef.current && !popupRef.current.closed) popupRef.current.close();
      setIsConnecting(false);
    }
  }, [open]);

  const handleReauth = () => {
    setIsConnecting(true);
    const origin = window.location.origin;
    // Server reads the user from the session cookie.
    const authUrl = `/api/gdrive/auth?origin=${encodeURIComponent(origin)}`;

    // Open the OAuth flow in a small popup so the user stays on the current page
    const popup = window.open(
      authUrl,
      "gdrive_reauth",
      "width=600,height=700,left=200,top=100,resizable=yes,scrollbars=yes"
    );
    popupRef.current = popup;

    if (!popup) {
      // Popup was blocked — fall back to full-page redirect
      window.location.href = authUrl;
      return;
    }

    // Poll every 500ms to detect when the popup closes
    pollRef.current = setInterval(() => {
      if (!popup || popup.closed) {
        if (pollRef.current) clearInterval(pollRef.current);
        setIsConnecting(false);
        // The popup redirected to /?gdrive_connected=1 before closing.
        // We trust that if the popup closed normally the auth succeeded.
        // The caller should re-check token status to be sure.
        onSuccess();
      }
    }, 500);
  };

  return (
    <AlertDialog open={open}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-amber-500" />
            Google Drive Re-Authentication Required
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-2">
            <span className="block">
              Your Google Drive connection has expired or been revoked. You need to
              re-connect before patient folders can be created.
            </span>
            <span className="block text-muted-foreground text-xs">
              Your local files and session data are safe. Re-connecting will not affect
              any files already saved locally.
            </span>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel} disabled={isConnecting}>
            Skip Drive (save locally only)
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleReauth}
            disabled={isConnecting}
            className="gap-2"
          >
            {isConnecting ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Connecting…
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" />
                Re-connect Google Drive
              </>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
